import { FC, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { AppHeader, MobileAuthHeader } from '~widgets/shared/app-header';
import { MobileNavigation, Navigation } from '~widgets/shared/navigation';

import { roleType, useUser } from '~entities/shared/user';
import { useIsMobileAuth, useResolvedMobileAuthTheme } from '~shared/lib/mobile-auth';
import { RoutesUrls } from '~shared/lib/router';
import { PageLayout, useWindowInnerWidth } from '~shared/ui';
import { useCollapsed } from '~features/shared/collapse';

const PARENT_MOBILE_BREAKPOINT = 768;

// Kindergarten cabinet roles that get the mobile bottom tab bar on small screens.
const DOO_MOBILE_ROLES: roleType[] = ['accelerator', 'directorPreschool', 'teacher'];

// Pages reachable inside the Tunduk WebView (mobileAuth=true). Anything else
// is closed off — the WebView is meant for the parent cabinet only.
const MOBILE_AUTH_ALLOWED_PREFIXES = [
  RoutesUrls.parentCabinet, // /parent/*
  RoutesUrls.parentHelp, // /help
  RoutesUrls.instructions, // /instructions
  RoutesUrls.feedback, // /feedback
  RoutesUrls.settings, // /cabinet/settings
  RoutesUrls.privacyPolicy, // /cabinet/privacyPolicy
  RoutesUrls.mobileAuth, // /mobile-auth (login)
  RoutesUrls.authCallback, // /auth/callback (Tunduk WebView callback)
  RoutesUrls.logout, // /cabinet/logout
];

const isAllowedInMobileAuth = (pathname: string): boolean =>
  MOBILE_AUTH_ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix));

export interface BaseLayoutProps extends Partial<ComponentWithChildren> {}

export const BaseLayout: FC<BaseLayoutProps> = ({ children }) => {
  const collapsed = useCollapsed();
  const isMobileAuth = useIsMobileAuth();
  const resolvedTheme = useResolvedMobileAuthTheme();
  const location = useLocation();
  const user = useUser();
  const windowWidth = useWindowInnerWidth();

  // Parent role on a small screen in a regular browser session (not the Tunduk
  // webview) gets the same UX polish + bottom tab bar as the webview, but keeps
  // the standard AppHeader and the default green palette.
  const isParentMobile =
    !isMobileAuth &&
    windowWidth <= PARENT_MOBILE_BREAKPOINT &&
    Boolean(user?.role?.includes('parent'));

  // Kindergarten staff on a small screen — gets the bottom tab bar with
  // home / kids / applications / attendance shortcuts.
  const isDooMobile =
    !isMobileAuth &&
    windowWidth <= PARENT_MOBILE_BREAKPOINT &&
    DOO_MOBILE_ROLES.some((role) => user?.role?.includes(role));

  useEffect(() => {
    if (isMobileAuth) {
      document.body.classList.add('mobile-auth');
    } else {
      document.body.classList.remove('mobile-auth');
    }

    if (isParentMobile) {
      document.body.classList.add('parent-mobile');
    } else {
      document.body.classList.remove('parent-mobile');
    }

    if (isDooMobile) {
      document.body.classList.add('doo-mobile');
    } else {
      document.body.classList.remove('doo-mobile');
    }

    if (resolvedTheme === 'dark') {
      document.body.setAttribute('data-theme', 'dark');
    } else {
      document.body.removeAttribute('data-theme');
    }
  }, [isMobileAuth, isParentMobile, isDooMobile, resolvedTheme]);

  if (isMobileAuth && !isAllowedInMobileAuth(location.pathname)) {
    return <Navigate to={RoutesUrls.parentCabinetHome} replace />;
  }

  return (
    <>
      <PageLayout
        navigation={isMobileAuth ? <MobileNavigation /> : <Navigation />}
        header={isMobileAuth ? <MobileAuthHeader /> : <AppHeader />}
        collapsed={collapsed}
      >
        {children}
        <Outlet />
      </PageLayout>

      {(isParentMobile || isDooMobile) && <MobileNavigation />}
    </>
  );
};
